import * as React from "react";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { inputToAzureProvider, inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpRadioGroup } from "../../../../gvc/testNewForm/CpRadioGroup";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { store } from "../../../../../store";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { EllipsisOutlined } from "@ant-design/icons";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { Mk8sCluster } from "../../../../../schema/types/mk8s/entity/mk8s";
import { getDefaultAzureInput } from "../../../draft/utils/utils";
import { NodePoolDetectedTags } from "../detectedTags";
import { NodePoolCoreButton } from "../nodePoolCoreButton";

interface Props {
  index: number;
}
export const AzureNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const [recommendedAmis, setRecommendedAmis] = React.useState([]);

  React.useEffect(() => {
    fetchRecommendedAmis();
  }, []);

  async function fetchRecommendedAmis() {
    try {
      const cluster: Mk8sCluster = {
        name: "discover",
        spec: {
          provider: {
            azure: inputToAzureProvider(getDefaultAzureInput()),
          },
          version: "1.35.3",
        },
      } as any;
      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "recommendedImages",
          cluster: cluster,
        },
      });
      const res = data.data.allow.map((r: any) => ({ label: r, value: r }));
      setRecommendedAmis(res);
    } catch (e) {
      setRecommendedAmis([]);
    }
  }

  const location = useCpFormValue(`azure.location`);
  const name = useCpFormValue(`azure.nodePools.${index}.name`);
  const imageType = useCpFormValue(`azure.nodePools.${index}.overrideImage.type`);
  const size = useCpFormValue(`azure.nodePools.${index}.size`);
  const subnetId = useCpFormValue(`azure.nodePools.${index}.subnetId`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool {name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={["azure.nodePools"]} />
          </CardTitle>
          <CardContent>
            <div>
              <NodePoolDetectedTags name={`azure.nodePools.${index}.labels`} />
              <CpFormItem>
                <CpLabel name={`azure.nodePools.${index}.name`}>Name</CpLabel>
                <CpInput name={`azure.nodePools.${index}.name`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`azure.nodePools.${index}.name`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`azure.nodePools.${index}.size`}>Size</CpLabel>
                <Space.Compact>
                  <CpInput name={`azure.nodePools.${index}.size`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("sizes")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`azure.nodePools.${index}.size`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`azure.nodePools.${index}.subnetId`}>Subnet ID</CpLabel>
                <Space.Compact>
                  <CpInput name={`azure.nodePools.${index}.subnetId`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("subnets")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`azure.nodePools.${index}.subnetId`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex gap-2">
                  <div className="flex flex-col">
                    <CpLabel name={`azure.nodePools.${index}.minSize`}>Min Size</CpLabel>
                    <CpInputNumber name={`azure.nodePools.${index}.minSize`} />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`azure.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                    <CpInputNumber name={`azure.nodePools.${index}.maxSize`} />
                  </div>
                </div>
                <CpErrorWarningArray names={[`azure.nodePools.${index}.minSize`, `azure.nodePools.${index}.maxSize`]} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name="skip">Override Image</CpLabel>
                <CpRadioGroup
                  className="mb-1"
                  name={`azure.nodePools.${index}.overrideImage.type`}
                  options={[
                    { label: "No Override", value: "none" },
                    { label: "Recommended", value: "recommended" },
                    { label: "Reference", value: "reference" },
                  ]}
                />
                {imageType === "reference" ? (
                  <>
                    <div className="flex flex-col mb-2">
                      <CpLabel name={`azure.nodePools.${index}.overrideImage.reference.publisher`}>Publisher</CpLabel>
                      <CpInput
                        name={`azure.nodePools.${index}.overrideImage.reference.publisher`}
                        style={{ width: 600 }}
                      />
                    </div>
                    <div className="flex flex-col mb-2">
                      <CpLabel name={`azure.nodePools.${index}.overrideImage.reference.offer`}>Offer</CpLabel>
                      <CpInput name={`azure.nodePools.${index}.overrideImage.reference.offer`} style={{ width: 600 }} />
                    </div>
                    <div className="flex flex-col mb-2">
                      <CpLabel name={`azure.nodePools.${index}.overrideImage.reference.sku`}>SKU</CpLabel>
                      <CpInput name={`azure.nodePools.${index}.overrideImage.reference.sku`} style={{ width: 600 }} />
                    </div>
                    <div className="flex flex-col mb-2">
                      <CpLabel name={`azure.nodePools.${index}.overrideImage.reference.version`}>Version</CpLabel>
                      <CpInput
                        name={`azure.nodePools.${index}.overrideImage.reference.version`}
                        style={{ width: 600 }}
                      />
                    </div>
                  </>
                ) : imageType === "recommended" ? (
                  <CpSelect
                    name={`azure.nodePools.${index}.overrideImage.recommended`}
                    style={{ width: 600 }}
                    options={recommendedAmis}
                  />
                ) : null}
                <CpErrorWarningArray
                  names={[
                    `azure.nodePools.${index}.overrideImage.reference.publisher`,
                    `azure.nodePools.${index}.overrideImage.reference.offer`,
                    `azure.nodePools.${index}.overrideImage.reference.sku`,
                    `azure.nodePools.${index}.overrideImage.reference.version`,
                    `azure.nodePools.${index}.overrideImage.recommended`,
                  ]}
                />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`azure.nodePools.${index}.bootDiskSize`}>Boot Disk Size</CpLabel>
                <CpInputNumber name={`azure.nodePools.${index}.bootDiskSize`} placeholder={`30`} addonAfter={`GB`} />
                <CpErrorWarning name={`azure.nodePools.${index}.bootDiskSize`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`azure.nodePools.${index}.zones`}>Availability Zones</CpLabel>
                <CpSelect
                  mode={`multiple`}
                  name={`azure.nodePools.${index}.zones`}
                  options={[
                    { label: "1", value: "1" },
                    { label: "2", value: "2" },
                    { label: "3", value: "3" },
                  ]}
                  style={{ width: 180 }}
                />
                <CpErrorWarning name={`azure.nodePools.${index}.zonesroot`} />
              </CpFormItem>
              <div style={{ maxWidth: 900 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Labels</CpLabel>
                    <NodePoolCoreButton name={`azure.nodePools.${index}.labels`} />
                  </div>
                  <div className="-mt-2">
                    <CpArrayObject
                      factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                      labels={["Name", "Value"]}
                      name={`azure.nodePools.${index}.labels`}
                    >
                      <CpArrayItemNameValuePair />
                    </CpArrayObject>
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                  labels={["Key", "Value", "Effect"]}
                  label="Taints"
                  name={`azure.nodePools.${index}.taints`}
                >
                  <CpArrayItemTaint />
                </CpArrayObject>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "sizes" ? (
        <NGProviderBrowser
          width={1550}
          title="Browse Azure Sizes"
          initialValue={size}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: `sizes-${location}`,
                cluster: inputToMk8s(values),
              },
            });
            return data.sort((a: any, b: any) => {
              return a.vCPUs - b.vCPUs || a.MemoryGB - b.MemoryGB || a.name - b.name;
            });
          }}
          onOk={(value) => {
            formSetValue(formId, `azure.nodePools.${index}.size`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Name"]}
          distinctFilters={["Cores", "Memory", "Arch", "Tier"]}
          initialSort={["Cost per Month", "asc"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          labels={["Name", "Cost per Month", "Cores", "Memory", "Arch", "Tier"]}
          sortable
          getData={(item: any) => {
            return [
              item.name,
              item.name,
              [item.monthlyRate || 0, item.monthlyRate ? `$${item.monthlyRate.toFixed(2)}` : `No Cost Info`],
              item.vCPUs,
              [item.MemoryGB, formatSizeForProviderBrowser(item.MemoryGB, "GB")],
              item.CpuArchitectureType,
              item.tier,
            ];
          }}
        />
      ) : null}
      {browserKey === "subnets" ? (
        <NGProviderBrowser
          title="Browse Azure Subnets"
          initialValue={subnetId}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "subnets",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(formId, `azure.nodePools.${index}.subnetId`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Resource Group", "Id"]}
          getData={(item: any) => [item.id, item.name, item.resourceGroup, item.id]}
        />
      ) : null}
    </>
  );
};
